import type { FhbRuleSet } from "./types.js";

// first-home-scheme ruleset — rule period from 1 OCTOBER 2025.
//
// This is the period in which the federal Home Guarantee Scheme was RENAMED to
// the "Australian Government 5% Deposit Scheme": income caps removed, place
// limits removed, no LMI, and higher price caps (Sydney $1.5m, etc.). Effective
// 1 Oct 2025.  [figure-ledger #1, #3 — CITED, Housing Australia + firsthomebuyers.gov.au]
//
// QLD FHOG is at its $30,000 boost in this period (contracts 20 Nov 2023 –
// 30 Jun 2026). The reversion to $15,000 for contracts AFTER 30 Jun 2026 is a
// SEPARATE registered period (`tables-2026-07-01.ts`) — never a mutation of this
// one — so a historical assessment resolves against the rules in force then.
// [figure-ledger #4 — CITED, QRO]
//
// Every figure below is transcribed from the CITED primary sources in
// docs/research/discovery/verification/first-home-scheme-eligibility-figures.md.

const HOUSING_AUSTRALIA = {
  authority: "Housing Australia",
  url: "https://www.housingaustralia.gov.au/media/unlimited-places-higher-property-price-caps-first-home-buyers-1-october-2025",
} as const;

const ATO_FHSS = {
  authority: "Australian Taxation Office",
  url: "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/withdrawing-and-using-your-super/first-home-super-saver-scheme",
} as const;

const REVENUE_NSW_FHOG = {
  authority: "Revenue NSW",
  url: "https://www.revenue.nsw.gov.au/grants-schemes/first-home-owner-new-homes-grant",
} as const;
const SRO_VIC_FHOG = {
  authority: "State Revenue Office Victoria",
  url: "https://www.sro.vic.gov.au/buying-property/first-home-owner-grant",
} as const;
const QRO_FHOG = {
  authority: "Queensland Revenue Office",
  url: "https://qro.qld.gov.au/property-concessions-grants/first-home-grant/eligibility/",
} as const;

const REVENUE_NSW_DUTY = {
  authority: "Revenue NSW",
  url: "https://www.revenue.nsw.gov.au/grants-schemes/assistance-scheme",
} as const;
const SRO_VIC_DUTY = {
  authority: "State Revenue Office Victoria",
  url: "https://www.sro.vic.gov.au/buying-property/land-transfer-stamp-duty/concessions-exemptions-and-waivers/first-home-buyers/first-home-buyer-duty-exemption-or-concession",
} as const;
const QRO_DUTY = {
  authority: "Queensland Revenue Office",
  url: "https://qro.qld.gov.au/duties/transfer-duty/concessions/homes/first-home/",
} as const;

/**
 * Rule period from 1 Oct 2025: 5%-scheme rename live, QLD FHOG at $30k. Open
 * for the federal scheme + NSW/VIC tables; QLD FHOG closes at 30 Jun 2026 (the
 * next period swaps it to $15k), but the whole period record is superseded by
 * `tables-2026-07-01.ts` from 1 Jul 2026 so date resolution stays simple.
 */
export const tables_2025_10_01: FhbRuleSet = {
  periodKey: "2025-10-01",
  periodLabel: "from 1 October 2025 (5% Deposit Scheme rename; QLD FHOG $30k)",
  effectiveFrom: "2025-10-01",
  effectiveTo: "2026-06-30",
  sourcesVerifiedAt: "2026-06-22",

  // ── 1. Australian Government 5% Deposit Scheme (federal) ──
  // [#1 income caps removed 1 Oct 2025; #2 5%/2% deposit; #3 price caps]
  fivePercent: {
    name: "Australian Government 5% Deposit Scheme",
    minDeposit: 0.05,
    singleParentMinDeposit: 0.02,
    incomeCapsRemoved: true,
    incomeCapSingle: null,
    incomeCapCouple: null,
    // Cap table from the 1-Oct-2025 Housing Australia media release. Keyed by
    // capital-city / region group; v1 surfaces the NSW/VIC/QLD-relevant rows
    // (others carried for completeness of the federal table).
    priceCaps: {
      "Sydney/Newcastle/Illawarra": 1_500_000,
      "Melbourne/Geelong": 950_000,
      Brisbane: 1_000_000,
      "Gold Coast/Sunshine Coast": 1_000_000,
      Perth: 850_000,
      Adelaide: 900_000,
    },
    source: HOUSING_AUSTRALIA,
  },

  // ── 2. First Home Super Saver (federal, super) — information only ──
  fhss: {
    maxReleasable: 50_000,
    source: ATO_FHSS,
  },

  // ── 3. State First Home Owner Grant (new-build only in all three) ──
  // [#4 — CITED]
  fhog: {
    // NSW $10k: new home ≤$600k, or land+build ≤$750k. New-build only.
    NSW: {
      amount: 10_000,
      newBuildOnly: true,
      valueCap: 750_000,
      source: REVENUE_NSW_FHOG,
    },
    // VIC $10k: new home ≤$750k. New-build only.
    VIC: {
      amount: 10_000,
      newBuildOnly: true,
      valueCap: 750_000,
      source: SRO_VIC_FHOG,
    },
    // QLD $30k: contracts 20 Nov 2023 – 30 Jun 2026; new-build only; value <$750k.
    // Reverts to $15k for contracts after 30 Jun 2026 (next period).
    QLD: {
      amount: 30_000,
      newBuildOnly: true,
      valueCap: 750_000,
      source: QRO_FHOG,
    },
  },

  // ── 4. State stamp-duty first-home relief ──
  // [#5 — CITED]
  duty: {
    // NSW (FHBAS): full exemption ≤$800k, concession $800k–$1.0m. New+established.
    NSW: {
      fullExemptionCeiling: 800_000,
      concessionCeiling: 1_000_000,
      newBuildExemptRegardlessOfValue: false,
      source: REVENUE_NSW_DUTY,
    },
    // VIC: nil ≤$600k, taper $600,001–$750k. New+established.
    VIC: {
      fullExemptionCeiling: 600_000,
      concessionCeiling: 750_000,
      newBuildExemptRegardlessOfValue: false,
      source: SRO_VIC_DUTY,
    },
    // QLD: established home nil ≤$700k, concession $700k–$800k. AND new builds
    // are nil duty REGARDLESS of value from 1 May 2025 — modelled by the flag.
    QLD: {
      fullExemptionCeiling: 700_000,
      concessionCeiling: 800_000,
      newBuildExemptRegardlessOfValue: true,
      source: QRO_DUTY,
    },
  },
};
